import axios from "axios";
import jwt_decode from "jwt-decode";
import { initializeApp } from "firebase/app";
import {
   getAuth,
   signInWithCustomToken,
   signOut,
   SAMLAuthProvider,
   signInWithPopup,
   sendPasswordResetEmail,
   OAuthProvider,
   signInWithRedirect,
} from "firebase/auth";
import firebase from "firebase/compat/app";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { saveusercredentials, savenorthwelluserobj } from "../redux/actions/auth.action";
import { store } from "../redux/store";
import { isCognitoAuth } from "../auth/region";
import { forceReauth, getNorthwellUser, getStsTokens, saveStsTokens } from "../auth/session";
import { getToken } from "../Apimanager/Networking";
import { verifyToken } from "../api/onboarding";

let app;
let db;
let auth;

if (process.env.REACT_APP_IS_EU !== "true") {
   var configure = {
      apiKey: process.env.REACT_APP_FIREBASEAPIKEY,
      authDomain: process.env.REACT_APP_FIREBASEAUTHDOMAIN,
      storageBucket: process.env.REACT_APP_STORAGEBUCKET,
      projectId: process.env.REACT_APP_FIREBASEPROJECTID,
      // appId: process.env.REACT_APP_FIREBASE_APP_ID,
      // messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
   };


   firebase.initializeApp(configure);
   app = initializeApp(configure);
   db = getFirestore(app);
   auth = getAuth();
   window.firestore = db;
}

const dispatch = store.dispatch;
const SIGN_IN_TIMEOUT_MS = 20000;

const signInWithTimeout = (customToken, timeoutMs) =>
   Promise.race([
      signInWithCustomToken(auth, customToken),
      new Promise((resolve, reject) => {
         setTimeout(() => {
            reject(new Error("Timed out while signing in with custom token"));
         }, timeoutMs);
      }),
   ]);

const handleSignOut = async () => {
   try {
      await signOut(auth);
      console.log("User signed out successfully");
   } catch (error) {
      console.error("Error signing out:", error);
   }
};

const resetPasswordGlobal = async (emailAddress, success = () => { }, onError = () => { }) => {
   try {
      await sendPasswordResetEmail(auth, emailAddress);
      success();
   } catch (error) {
      onError(error);
   }
};

const handleSAMLSignIn = async (onSuccess, onError) => {
   try {
      const provider = new SAMLAuthProvider(process.env.REACT_APP_FIREBASEAUTHPROVIDER);
      const res = await signInWithPopup(auth, provider);

      const user = res.user;
      if (user) {
         onSuccess(res);
      } else {
         onError("error");
      }
   } catch (error) {
      onError(error);
      console.error("SAML sign-in error:", error);
   }
};

const handleWheelerSignIn = async (onSuccess, onError, providerId) => {
   try {
      const provider = new SAMLAuthProvider(providerId);
      const res = await signInWithPopup(auth, provider);
      dispatch(savenorthwelluserobj(JSON.parse(JSON.stringify(res))));
      const user = res.user;
      if (user) {
         onSuccess(res);
      } else {
         onError("error");
      }
   } catch (error) {
      onError(error);
      console.error("SAML sign-in error:", error);
   }
};

const handleSSOSignIn = async (authO, onSuccess, onError) => {
   try {
      const provider = new OAuthProvider(authO);
      const res = await signInWithRedirect(auth, provider);

      const user = res.user;
      if (user) {
         onSuccess(res);
      } else {
         onError("error");
      }
   } catch (error) {
      onError(error);
      console.error("SAML sign-in error:", error);
   }
};

const getFirestoreData = async (collection, document) => {
   try {
      const docRef = doc(db, collection, document); // Reference to the document
      const docSnap = await getDoc(docRef); // Get document snapshot

      if (docSnap.exists()) {
         return docSnap;
      } else {
         return docSnap;
      }
   } catch (error) {
      return [];
   }
};

const signInWithCustomTokenCustom = (
   customToken,
   success = () => { },
   errorcallback = () => { },
   options = {}
) => {
   const { timeoutMs = SIGN_IN_TIMEOUT_MS } = options;

   if (!customToken) {
      const tokenError = new Error("Custom token is missing");
      errorcallback(tokenError);
      return Promise.reject(tokenError);
   }

   return signInWithTimeout(customToken, timeoutMs)
      .then((storeDataRedux) => {
         success(storeDataRedux);
         dispatch(savenorthwelluserobj(JSON.parse(JSON.stringify(storeDataRedux))));
         return storeDataRedux;
      })
      .catch(async (error) => {
         // Recover from stuck/corrupt Firebase auth persistence without retry loops.
         if (error?.message?.toLowerCase()?.includes("timed out")) {
            try {
               await signOut(auth);
            } catch (signOutError) {
               console.log("signOut after timeout failed", signOutError);
            }
            indexedDB.deleteDatabase("firebaseLocalStorageDb");
         }
         errorcallback(error);
         throw error;
      });
};

async function triggerLogout() {
   const { logout } = await import("../Apimanager/Networking");
   return logout();
}

export async function refreshSession() {
   const stsTokens = getStsTokens();
   const refreshToken = stsTokens?.refreshToken || "";
   const bodyFormData = {
      grant_type: "refresh_token",
      refresh_token: refreshToken,
   };

   return axios({
      method: "post",
      url: `https://securetoken.googleapis.com/v1/token?key=${process.env.REACT_APP_FIREBASEAPIKEY}`,
      data: bodyFormData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
   })
      .then((response) => {
         const stsTokenManager = {
            apiKey: process.env.REACT_APP_FIREBASEAPIKEY,
            refreshToken: response.data.refresh_token,
            accessToken: response.data.access_token,
            expirationTime: response.data.expires_in,
         };
         return stsTokenManager;
      })
      .catch((error) => {
         console.log("refresh token error " + error);
         forceReauth();
      });
}

export async function getAccessToken() {
   const northwelluser = getNorthwellUser();
   let stsTokens = getStsTokens();

   if (stsTokens) {
      let accessTokenExpTime;

      try {
         accessTokenExpTime = jwt_decode(stsTokens.accessToken).exp;
      } catch (error) {
         await triggerLogout();
         return;
      }

      if (typeof accessTokenExpTime !== "number" || Number.isNaN(accessTokenExpTime)) {
         await triggerLogout();
         return;
      }

      const now = parseInt(Date.now() / 1000, 10);

      if (now >= accessTokenExpTime - 30) {
         const newStsTokens = await refreshSession();

         if (!newStsTokens || !newStsTokens.accessToken) {
            await triggerLogout();
            return;
         }

         saveStsTokens(newStsTokens);
         return newStsTokens.accessToken;
      }

      return stsTokens.accessToken;
   }

   if (northwelluser && stsTokens === undefined) {
      await triggerLogout();
      return;
   }

   if (stsTokens !== null && stsTokens !== undefined) {
      await triggerLogout();
   }
}

export const refreshReduxStoreOnboard = async (getProviderData) => {
   const token = await getToken();
   if (!token) {
      return;
   }

   try {
      const response = await verifyToken({ firebaseToken: token });
      if (response) {
         signInWithCustomTokenCustom(
            response.customToken,
            () => {
               const userData = { user: response.userData };
               getProviderData(userData, false, () => { }, true);
               store.dispatch(saveusercredentials(userData));
            },
            (error) => {
               console.log(error);
            }
         );
      }
   } catch (error) {
      console.log(error);
   }
};

export {
   auth,
   signInWithCustomTokenCustom,
   getFirestoreData,
   handleSignOut,
   handleSAMLSignIn,
   resetPasswordGlobal,
   handleSSOSignIn,
   handleWheelerSignIn,
};
